import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { blogService } from '../../../../services/blogService';
import { storageService } from '../../../../services/storageService';

import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';

const BlogForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    image: '',
    imageAlt: '',
    category: '',
    categoryId: '',
    status: 'draft',
    readTime: 5,
    featured: false,
    scheduledPublishDate: ''
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadError, setImageUploadError] = useState(null);
  const [uploadingFeaturedImage, setUploadingFeaturedImage] = useState(false);
  const [featuredImageUploadError, setFeaturedImageUploadError] = useState(null);

  useEffect(() => {
    if (isEdit) {
      fetchBlog();
    }
  }, [id]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const data = await blogService?.getBlogById(id);
      setFormData({
        title: data?.title || '',
        excerpt: data?.excerpt || '',
        content: data?.content || '',
        image: data?.image || '',
        imageAlt: data?.imageAlt || '',
        category: data?.category || '',
        categoryId: data?.categoryId || '',
        status: data?.status || 'draft',
        readTime: data?.readTime || 5,
        featured: data?.featured || false,
        scheduledPublishDate: data?.scheduledPublishDate ? new Date(data?.scheduledPublishDate)?.toISOString()?.slice(0, 16) : ''
      });
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes?.includes(file?.type)) {
      setImageUploadError('Please select a valid image file (JPEG, PNG, WebP, or GIF)');
      return;
    }

    // Validate file size (5MB)
    if (file?.size > 5242880) {
      setImageUploadError('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingImage(true);
      setImageUploadError(null);
      
      const imageUrl = await storageService?.uploadBlogImage(file);
      
      // Insert image markdown at cursor position or end of content
      const textarea = document.getElementById('content');
      const cursorPos = textarea?.selectionStart || formData?.content?.length;
      const imageMarkdown = `\n![Image description](${imageUrl})\n`;
      
      const newContent = 
        formData?.content?.slice(0, cursorPos) + 
        imageMarkdown + 
        formData?.content?.slice(cursorPos);
      
      setFormData(prev => ({
        ...prev,
        content: newContent
      }));
      
      // Reset file input
      e.target.value = '';
    } catch (err) {
      console.error('Error uploading image:', err);
      setImageUploadError(err?.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFeaturedImageUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes?.includes(file?.type)) {
      setFeaturedImageUploadError('Please select a valid image file (JPEG, PNG, WebP, or GIF)');
      return;
    }

    // Validate file size (5MB)
    if (file?.size > 5242880) {
      setFeaturedImageUploadError('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingFeaturedImage(true);
      setFeaturedImageUploadError(null);
      
      const imageUrl = await storageService?.uploadBlogImage(file, 'featured');
      
      setFormData(prev => ({
        ...prev,
        image: imageUrl
      }));
      
      // Reset file input
      e.target.value = '';
    } catch (err) {
      console.error('Error uploading featured image:', err);
      setFeaturedImageUploadError(err?.message || 'Failed to upload featured image');
    } finally {
      setUploadingFeaturedImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const submitData = { ...formData };
      
      // Convert scheduled date to ISO string if provided, otherwise set to null
      if (submitData?.scheduledPublishDate && submitData?.scheduledPublishDate?.trim() !== '') {
        submitData.scheduledPublishDate = new Date(submitData?.scheduledPublishDate)?.toISOString();
      } else {
        submitData.scheduledPublishDate = null;
      }

      if (isEdit) {
        await blogService?.updateBlog(id, submitData);
      } else {
        await blogService?.createBlog(submitData);
      }
      navigate('/admin/dashboard/blogs');
    } catch (err) {
      console.error('Error saving blog:', err);
      setError(err?.message || 'Failed to save blog post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{isEdit ? 'Edit Blog' : 'New Blog'} - Admin Dashboard</title>
      </Helmet>
      <div className="min-h-screen bg-muted/30">
        {/* Header */}
        <header className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Link to="/admin/dashboard/blogs">
                  <Button variant="ghost" size="sm" iconName="ArrowLeft" />
                </Link>
                <h1 className="text-lg md:text-xl font-bold text-foreground">
                  {isEdit ? 'Edit Blog Post' : 'New Blog Post'}
                </h1>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-card rounded-xl border border-border p-6 md:p-8 space-y-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-foreground mb-2">
                Title *
              </label>
              <Input
                id="title"
                name="title"
                type="text"
                placeholder="Enter blog title"
                value={formData?.title}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="excerpt" className="block text-sm font-medium text-foreground mb-2">
                Excerpt *
              </label>
              <textarea
                id="excerpt"
                name="excerpt"
                rows={3}
                placeholder="Brief description of the blog post"
                value={formData?.excerpt}
                onChange={handleChange}
                required
                disabled={loading}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="content" className="block text-sm font-medium text-foreground">
                  Content *
                </label>
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="image-upload"
                    className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-foreground bg-background border border-border rounded-lg hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploadingImage ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent"></div>
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                          <polyline points="21 15 16 10 5 21"></polyline>
                        </svg>
                        <span>Add Image</span>
                      </>
                    )}
                  </label>
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleImageUpload}
                    disabled={uploadingImage || loading}
                    className="hidden"
                  />
                </div>
              </div>
              {imageUploadError && (
                <div className="mb-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-sm text-red-600 dark:text-red-400">
                  {imageUploadError}
                </div>
              )}
              <textarea
                id="content"
                name="content"
                rows={12}
                placeholder="Write your blog content here...\n\nYou can add images using the 'Add Image' button above. Images will be inserted as markdown: ![Image description](url)"
                value={formData?.content}
                onChange={handleChange}
                required
                disabled={loading}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent font-mono text-sm"
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Tip: Use markdown syntax for formatting. Images are inserted as: ![Alt text](image-url)
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-foreground mb-2">
                  Category *
                </label>
                <Input
                  id="category"
                  name="category"
                  type="text"
                  placeholder="e.g., Web Development"
                  value={formData?.category}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="categoryId" className="block text-sm font-medium text-foreground mb-2">
                  Category ID *
                </label>
                <Input
                  id="categoryId"
                  name="categoryId"
                  type="text"
                  placeholder="e.g., web-development"
                  value={formData?.categoryId}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="image" className="block text-sm font-medium text-foreground mb-2">
                Featured Image
              </label>
              <div className="flex gap-2">
                <Input
                  id="image"
                  name="image"
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={formData?.image}
                  onChange={handleChange}
                  disabled={loading}
                  className="flex-1"
                />
                <label
                  htmlFor="featured-image-upload"
                  className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-lg hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {uploadingFeaturedImage ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent"></div>
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="17 8 12 3 7 8"></polyline>
                        <line x1="12" y1="3" x2="12" y2="15"></line>
                      </svg>
                      <span>Upload</span>
                    </>
                  )}
                </label>
                <input
                  id="featured-image-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleFeaturedImageUpload}
                  disabled={uploadingFeaturedImage || loading}
                  className="hidden"
                />
              </div>
              {featuredImageUploadError && (
                <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-sm text-red-600 dark:text-red-400">
                  {featuredImageUploadError}
                </div>
              )}
              <p className="mt-2 text-xs text-muted-foreground">
                Upload an image or enter a URL manually
              </p>
            </div>

            <div>
              <label htmlFor="imageAlt" className="block text-sm font-medium text-foreground mb-2">
                Image Alt Text
              </label>
              <Input
                id="imageAlt"
                name="imageAlt"
                type="text"
                placeholder="Describe the image for accessibility"
                value={formData?.imageAlt}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="readTime" className="block text-sm font-medium text-foreground mb-2">
                  Read Time (minutes)
                </label>
                <Input
                  id="readTime"
                  name="readTime"
                  type="number"
                  min="1"
                  value={formData?.readTime}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-foreground mb-2">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData?.status}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div>
                <label htmlFor="scheduledPublishDate" className="block text-sm font-medium text-foreground mb-2">
                  Scheduled Publish Date
                </label>
                <Input
                  id="scheduledPublishDate"
                  name="scheduledPublishDate"
                  type="datetime-local"
                  value={formData?.scheduledPublishDate}
                  onChange={handleChange}
                  disabled={loading}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Leave empty for immediate publishing. Blog will auto-publish at scheduled time.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-6 border-t border-border">
              <Button
                type="submit"
                variant="default"
                disabled={loading}
                iconName={loading ? undefined : 'Save'}
                iconPosition="left"
              >
                {loading ? 'Saving...' : isEdit ? 'Update Blog Post' : 'Create Blog Post'}
              </Button>
              <Link to="/admin/dashboard/blogs">
                <Button type="button" variant="outline" disabled={loading}>
                  Cancel
                </Button>
              </Link>
            </div>
          </form>
        </main>
      </div>
    </>
  );
};

export default BlogForm;
